import React, { useEffect, useState, useCallback } from "react";
import { Plus, Wallet, FileDown, FileSpreadsheet, ChevronLeft, ChevronRight, Settings2, TrendingUp, TrendingDown, Scale } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError, trWeekday } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CashEntryForm from "@/components/cashbook/CashEntryForm";
import CashRulesDialog from "@/components/cashbook/CashRulesDialog";
import CashGroupChart from "@/components/cashbook/CashGroupChart";
import CashEntriesTable from "@/components/cashbook/CashEntriesTable";
import CashBalanceStrip from "@/components/cashbook/CashBalanceStrip";
import CashMonthlyCompare from "@/components/cashbook/CashMonthlyCompare";
import ChequePortfolio from "@/components/cashbook/ChequePortfolio";
import SearchBox, { matches } from "@/components/SearchBox";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PERIODS = [["day", "Günlük"], ["month", "Aylık"], ["6m", "6 Aylık"], ["12m", "Yıllık"]];
const todayISO = () => new Date().toISOString().slice(0, 10);
const shift = (d, period, n) => { const x = new Date(`${d}T00:00:00`); if (period === "day") x.setDate(x.getDate() + n); else x.setMonth(x.getMonth() + n * ({ "6m": 6, "12m": 12 }[period] || 1)); return x.toISOString().slice(0, 10); };

export default function Cashbook() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [period, setPeriod] = useState("day");
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState({ expense: {}, income: {} });
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [view, setView] = useState("cash");
  const groupName = (r) => groups[r.kind === "income" ? "income" : "expense"]?.[r.group] || r.group;
  const shownRows = (data?.rows || []).filter((r) => matches(search, r.description, groupName(r), r.account_name, r.method, r.note, r.amount));

  const load = useCallback(async () => {
    const [r, a] = await Promise.all([api.get("/cashbook/entries", { params: { period, date } }), api.get("/cashbook/accounts")]);
    setData(r.data); setAccounts(a.data);
  }, [period, date]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/cashbook/groups").then((r) => setGroups(r.data)); }, []);

  const del = async (id) => {
    if (!window.confirm("Kayıt silinsin mi?")) return;
    try { await api.delete(`/cashbook/entries/${id}`); toast.success("Kayıt silindi"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const s = data?.summary;
  const exportUrl = (fmt) => `${BACKEND}/api/cashbook/export?period=${period}&date=${date}&format=${fmt}`;

  return (
    <div className="space-y-6" data-testid="cashbook-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Kasa Defteri</h1>
          <p className="text-sm text-muted-foreground mt-1">Gelir ve giderler; müşteri tahsilatları{isAdmin ? ", personel ödemeleri ve maliyet girişleri" : ""} otomatik eklenir. Grup, açıklamadan otomatik seçilir.</p>
        </div>
        {view === "cash" && <div className="flex flex-wrap gap-2">
          {isAdmin && <>
            <Button variant="outline" onClick={() => setRulesOpen(true)} data-testid="cashbook-rules-btn" className="rounded-full h-11 gap-2"><Settings2 className="h-4 w-4" /> Grup Kuralları</Button>
            <a href={exportUrl("pdf")} target="_blank" rel="noreferrer" data-testid="cashbook-export-pdf"><Button variant="outline" className="rounded-full h-11 gap-2"><FileDown className="h-4 w-4" /> PDF</Button></a>
            <a href={exportUrl("xlsx")} target="_blank" rel="noreferrer" data-testid="cashbook-export-xlsx"><Button variant="outline" className="rounded-full h-11 gap-2"><FileSpreadsheet className="h-4 w-4" /> Excel</Button></a>
          </>}
          <Button onClick={() => { setEditItem(null); setFormOpen(true); }} data-testid="cashbook-add-btn" className="rounded-full h-11 px-6 font-semibold gap-2"><Plus className="h-4 w-4" /> Kayıt Ekle</Button>
        </div>}
      </div>

      <div className="inline-flex rounded-xl bg-muted/50 p-1 w-fit" data-testid="cashbook-view-tabs">
        <button onClick={() => setView("cash")} data-testid="cashbook-view-cash" className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors ${view === "cash" ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Kasa Hareketleri</button>
        <button onClick={() => setView("cheque")} data-testid="cashbook-view-cheque" className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors ${view === "cheque" ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Çek Portföyü</button>
      </div>

      {view === "cheque" && <ChequePortfolio />}

      {view === "cash" && <>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="inline-flex rounded-xl bg-muted/50 p-1" data-testid="cashbook-period-tabs">
          {PERIODS.map(([id, label]) => (
            <button key={id} onClick={() => setPeriod(id)} data-testid={`cashbook-period-${id}`}
              className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors ${period === id ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setDate(shift(date, period, -1))} data-testid="cashbook-prev" className="h-9 w-9 rounded-lg hover:bg-accent inline-flex items-center justify-center"><ChevronLeft className="h-4 w-4" /></button>
          <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} data-testid="cashbook-date" className="h-9 w-40 font-mono" />
          <span className="text-xs text-muted-foreground font-medium" data-testid="cashbook-weekday">{trWeekday(date)}</span>
          <button onClick={() => setDate(shift(date, period, 1))} data-testid="cashbook-next" className="h-9 w-9 rounded-lg hover:bg-accent inline-flex items-center justify-center"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <span className="text-sm font-semibold text-muted-foreground" data-testid="cashbook-label">{data?.label}</span>
      </div>

      {s?.accounts && <CashBalanceStrip summary={s} onChanged={load} period={period} date={date} />}

      {s && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Kpi label="Gelir" value={s.income} icon={TrendingUp} tone="text-emerald-400" testId="cashbook-kpi-income" />
          <Kpi label="Gider" value={s.expense} icon={TrendingDown} tone="text-rose-400" testId="cashbook-kpi-expense" />
          <Kpi label="Net" value={s.net} icon={Scale} tone={s.net >= 0 ? "text-emerald-400" : "text-rose-400"} testId="cashbook-kpi-net" />
        </div>
      )}

      {s && <CashGroupChart summary={s} period={period} />}
      {s && period === "12m" && <CashMonthlyCompare summary={s} />}

      <SearchBox value={search} onChange={setSearch} placeholder="Açıklama, grup, hesap ara…" testId="cashbook-search-input" className="w-full sm:w-[320px]" />

      <CashEntriesTable rows={shownRows} onEdit={(r) => { setEditItem(r); setFormOpen(true); }} onDelete={del} />
      </>}

      <CashEntryForm open={formOpen} onClose={() => setFormOpen(false)} item={editItem} groups={groups} accounts={accounts} defaultDate={period === "day" ? date : todayISO()} onSaved={load} />
      {isAdmin && <CashRulesDialog open={rulesOpen} onClose={() => setRulesOpen(false)} groups={groups} />}
    </div>
  );
}

const Kpi = ({ label, value, icon: Icon, tone, testId }) => (
  <Card className="p-5 flex items-center gap-4" data-testid={testId}>
    <div className={`h-11 w-11 rounded-xl bg-muted/60 flex items-center justify-center ${tone}`}><Icon className="h-5 w-5" /></div>
    <div><div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div><div className={`font-mono text-2xl font-bold ${tone}`}>{TRY(value)}</div></div>
  </Card>
);

export { Wallet };
